/**
 * Layout pipeline for the `FlowChart` component.
 *
 * These functions are pure: they take plain data and return plain data, with no React and no
 * DOM.  That makes them testable without jsdom, which is the only practical way to get the
 * component under test (see `layout.test.ts`).
 *
 * NOTE: this module is deliberately NOT re-exported from `index.tsx`.  That file's exports flow
 * through `src/viewer/components/index.tsx` into `src/library.ts`, which is the published
 * `@qerent/lib` plugin SDK surface — anything exported there becomes a permanent public symbol
 * and a resolvable template tag name.  Keep this module private to the component.
 */
import React from "react";
import { Edge as BaseEdge, Node } from "@xyflow/react";
import { computeHops, type RouteInput } from "./crossings";
import { chartRadius } from "./edgeTypes/path";
import {
    ARROWHEAD_CLEARANCE, Occupancy, PIN_OVERRIDE, alongAxis, routeEdge, routeIsClear, runBounds, runHits, sideMidpoint, simplify, straighten,
    type Obstacle, type PortRequest, type RouteResult, type SoftRect,
} from "./router";
import { NODE_CONTENT_PADDING, shapeInset } from "./shapeGeometry";


/** Mirrors react-flow's `MarkerType` without importing it, so an upgrade cannot silently
 *  change the values the `metadata` block documents. */
type MarkerKind = "arrow" | "arrowclosed";

/**
 * A marker given in full rather than by name.
 *
 * Declared here rather than beside `normaliseMarker` because `FlowChartLink` needs it: the object
 * form has always been documented and always been accepted at runtime, but the link type only
 * admitted the two names, so every caller using it had to cast.
 */
type EdgeMarker = { type: MarkerKind; width?: number; height?: number; color?: string };

/**
 * A side of a node.
 *
 * The one name for this concept.  `HandleLocation` (below), `ShapeSide` (shapeGeometry.ts) and a
 * local `Side` (nodeTypes/factory.tsx) were all byte-identical copies; they are now aliases, kept
 * because each one is emitted into the published `.d.ts` tree and removing a name there is a
 * breaking change for tenant plugins.
 */
type HandlePosition = "top" | "bottom" | "left" | "right";

/** The same four sides as a value, so an authored string can be checked against them. */
const SIDES: HandlePosition[] = ["top", "bottom", "left", "right"];

/**
 * An authored side, or `undefined` where it is not one of the four.
 *
 * A link's `positionStart` / `positionEnd` and the caller's handle mapping tables are plain JSON:
 * nothing stops a template writing `"Left"`.  Those values end up in a handle id, get parsed back
 * out of it, and are then used to index `SIDE_NORMALS` -- so an unrecognised one used to throw out
 * of the router and take the whole layout with it.  Ignored rather than reported, which is
 * what `requiredPorts` already does with the same values on the way in to the router.
 */
const asSide = (value: unknown): HandlePosition | undefined =>
    SIDES.includes(value as HandlePosition) ? value as HandlePosition : undefined;

type FlowChartLink = {
    /**
     * Distinguishes two links between the same pair of nodes.  Optional; without one a link is
     * keyed on `from` + `to`, and a second link between the same ordered pair is discarded.
     */
    id?: string;
    from?: string;
    to?: string;
    /**
     * Whether this link follows the route the layout engine computed for it, or is drawn as a
     * plain point-to-point path.  Optional - defaults to `elk`.  A `straight` link ignores the
     * route regardless.
     */
    routing?: "elk" | "direct";
    /**
     * How this link is drawn where its path crosses another link's.  Links are painted in the
     * order they are declared, so a link always crosses *over* the links declared before it.
     * Optional - defaults to `none`.
     */
    crossing?: "none" | "casing" | "hop";
    type?: string;
    color?: string;
    width?: number;
    className?: string;
    style?: React.CSSProperties;
    animated?: boolean;
    /**
     * Either a name or a full marker object; `normaliseMarker` accepts both.  `null` means "no
     * marker", which is the only way to suppress the `arrowclosed` that `markerEnd` defaults to.
     */
    markerStart?: MarkerKind | EdgeMarker | null;
    markerEnd?: MarkerKind | EdgeMarker | null;
    positionStart?: HandlePosition;
    positionEnd?: HandlePosition;

    label?: string;
    content?: React.ReactNode;
    excludeFromLayout?: boolean;

    /**
     * @deprecated use `content` instead.
     */
    template?: string;
};

type FlowChartNode = {
    id: string;
    /** Optional - defaults to `default`, which draws a rectangle.  `mapInputData` fills it in. */
    type?: string;
    /**
     * Optional - defaults to `false`.  Tested for truthiness everywhere it is consumed, which is
     * what lets a template send the string `"true"` (see `compatibility.test.ts`).
     */
    hidden?: boolean;
    links?: FlowChartLink[];
    children?: FlowChartNode[];
    className?: string;
    style?: any;
    x?: number;
    y?: number;

    label?: string;
    content?: React.ReactNode;
    /** Proportion of the shape filled from the bottom, 0-100.  Consumed by every shape node. */
    fillPercentage?: number;

    /**
     * @deprecated use `content` instead.
     */
    template?: string;
};

/** @deprecated use {@link HandlePosition}; kept because it ships in the published `.d.ts`. */
type HandleLocation = HandlePosition;

type HandleMapping = {
    "left-straight": HandleLocation;
    "left-up": HandleLocation;
    "left-down": HandleLocation;

    "right-straight": HandleLocation;
    "right-up": HandleLocation;
    "right-down": HandleLocation;

    "straight-up": HandleLocation;
    "straight-down": HandleLocation;
};

type LayoutOptions = {
    /**
     * Whether the chart lays itself out or honours the `x`/`y` on each node.
     *
     * Optional.  When omitted, a node carrying a non-zero coordinate switches the whole chart to
     * `manual` -- which is how this has always behaved, but it is implicit, it cannot express
     * "manual, at the origin", and one stray coordinate disables layout for every other node.
     * Prefer setting it explicitly; the fallback is kept for charts written before it existed.
     */
    layout?: "auto" | "manual";
    /** Optional - defaults to `LR`. */
    rankdir?: "TB" | "BT" | "LR" | "RL";
    /** Optional - `null` (the default) means centred, which is what ELK calls BALANCED. */
    align?: "UL" | "UR" | "DL" | "DR" | null;
    /** Optional - defaults to `network-simplex`. */
    ranker?: "network-simplex" | "tight-tree" | "longest-path";
    /** Optional - defaults to `32`. */
    nodesep?: number;
    /** Optional - defaults to `32`. */
    edgesep?: number;
    /** Optional - defaults to `32`. */
    ranksep?: number;
    includeInterGroupLinksInLayout?: boolean;
    /**
     * Whether links ARRIVING at the same node are merged into one trunk.
     *
     * Off by default.  When on, links reaching a node by the same side meet it at the same point
     * and share the run leading up to it, so a fan-in reads as one arrow rather than several
     * landing side by side.  A link the router brings in by a different side keeps its own
     * arrival: merging it would mean routing it badly to get there.
     *
     * Links LEAVING a node are deliberately untouched.  This used to be `elk.layered.mergeEdges`,
     * which merges both ends, and the departure half is destructive: every link out of a node is
     * forced onto one point placed to suit the FARTHEST branch, so a link to a near neighbour is
     * dragged onto a trunk it has no use for and has to come back.  Measured on a real plant, a
     * straight 58px link between adjacent nodes became a 183px path with four bends, and the chart
     * gained 6% more line overall.
     */
    mergeLinks?: boolean;
    /**
     * How the corners of every link are drawn.  Optional - defaults to `rounded`.  A chart draws
     * all its links one way, whatever each link's `type` says; `mixed` leaves it to the type.
     */
    corners?: "rounded" | "sharp" | "mixed";

    /**
     * @deprecated Only consulted for links the router did not route.
     *
     * These tables map a coarse "which way is the target" reading into a node side.  Where ELK
     * produced a route, the edge attaches wherever that route actually starts and ends and the
     * tables are ignored.  They still apply to a `manual` layout, which ELK cannot route, and to
     * any link left out of the layout -- which by default is every inter-group link.  Use a
     * link's `positionStart` / `positionEnd` to pin a side instead: those become real routing
     * constraints rather than an after-the-fact override.
     */
    fromHandleMappings?: Partial<HandleMapping>;
    /** @deprecated see `fromHandleMappings`. */
    toHandleMappings?: Partial<HandleMapping>;
    /**
     * Advanced.  Raw ELK layout options, merged over everything derived from the fields above.
     * Keys are ELK option ids and values are strings.  Not covered by the compatibility
     * guarantees that apply to the other fields.
     */
    elkOptions?: Record<string, string>;
};

/**
 * `LayoutOptions` after `mergeLayoutOptions` has filled in the defaults.
 *
 * Every field a caller may omit is optional on `LayoutOptions`, because that is what callers
 * actually send -- the docs-site examples pass `{ rankdir, nodesep, ranksep }` and the Operational
 * Planning popover builds its object one slider at a time.  The layout internals, though, read
 * `nodesep` and friends unconditionally, so they take this instead: the type now says out loud
 * that the options must be merged before the engine sees them.
 *
 * `layout` and `elkOptions` stay optional: absence is meaningful for both.
 *
 * The two mapping tables come back COMPLETE, which is the whole point of merging them deeply --
 * `LayoutOptions` accepts a partial table because that is what a caller overriding one direction
 * writes, and the merge fills the other seven back in.
 */
type ResolvedLayoutOptions =
    Omit<LayoutOptions, "fromHandleMappings" | "toHandleMappings">
    & Required<Pick<LayoutOptions,
        "rankdir" | "align" | "ranker" | "nodesep" | "edgesep" | "ranksep"
        | "mergeLinks" | "includeInterGroupLinksInLayout" | "corners">>
    & { fromHandleMappings: HandleMapping; toHandleMappings: HandleMapping };

// v12 constrains an edge's data to `Record<string, unknown>`, where v11 left it open.
type Edge<T extends Record<string, unknown> = Record<string, any>> = BaseEdge<T> & { excludeFromLayout?: boolean };

const defaultLayoutOptions: ResolvedLayoutOptions = {
    rankdir: "LR",
    align: null, // center
    ranker: "network-simplex",
    nodesep: 32,
    edgesep: 32,
    ranksep: 32,
    mergeLinks: false,
    includeInterGroupLinksInLayout: false,
    corners: "rounded",
    // `layout` deliberately has NO default here.  `getLayoutedElements` distinguishes "unset"
    // from "auto" so that a node carrying an x/y can still switch the chart to manual, which is
    // the deprecated-but-supported implicit form.  A default would silence it.
    fromHandleMappings: {
        "left-straight": "left",
        "left-up": "left",
        "left-down": "bottom",

        "right-straight": "right",
        "right-up": "right",
        "right-down": "right",

        "straight-up": "top",
        "straight-down": "bottom",
    } as HandleMapping,
    toHandleMappings: {
        "left-straight": "right",
        "left-up": "bottom",
        "left-down": "top",

        "right-straight": "left",
        "right-up": "left",
        "right-down": "top",

        "straight-up": "bottom",
        "straight-down": "top",
    } as HandleMapping,
};
const defaultNodeOptions = {
    // "default" rather than "rectangle": both resolve to the Rectangle shape, but "default" is
    // also react-flow's own fallback name, so the emitted `react-flow__node-default` class is
    // unchanged.  This used to be "process", which is not a registered type at all -- react-flow
    // logged error 003 and fell back to its own built-in node, which renders with visible handles
    // and none of the `.FlowChart-node` / `.node-content` structure page CSS relies on.
    type: "default"
};
const defaultEdgeOptions = {
    type: "smoothstep",
    color: "var(--colors-grey-4)",
    width: 4,
    markerEnd: "arrowclosed" as MarkerKind,
    excludeFromLayout: false,
};

/**
 * Node dimensions, across both react-flow versions and both layout branches.
 *
 * v11 wrote measured dimensions to `node.width`/`node.height`; v12 writes them to
 * `node.measured` and leaves the top-level fields for author-set sizes.  Dagre additionally
 * writes `width`/`height` onto compound parents during layout (it is handed the live react-flow
 * node objects as its labels), so an explicit value must win over the measurement.
 */
const nodeWidth = (node: any): number => node?.width ?? node?.measured?.width;
const nodeHeight = (node: any): number => node?.height ?? node?.measured?.height;

/**
 * The box react-flow is currently DRAWING, as opposed to the one it measured.
 *
 * `getLayoutedElements` writes each node's laid-out size to `style`, because a top-level width in
 * v12 marks a node author-sized and stops it being re-measured.  So for anything that has already
 * been through a layout -- a group in particular, whose box ELK decided and whose DOM element is
 * sized to match -- `style` is the authority and the measurement merely agrees with it.
 */
const renderedWidth = (node: any): number => Number(node?.style?.width) || nodeWidth(node) || 0;
const renderedHeight = (node: any): number => Number(node?.style?.height) || nodeHeight(node) || 0;

/** Below this separation on an axis, two nodes count as aligned rather than offset. */
const HANDLE_DEAD_BAND = 40;

/**
 * Group geometry, previously two constants that disagreed with each other: `32 +` in the
 * fixed-position branch and `- 20` in the Dagre branch (whose comment said 32).  Named here so the
 * two layout branches agree.
 *
 * `.react-flow__node-group` in FlowChart.scss still carries `padding: 8px; margin: -8px` with
 * `box-sizing: content-box`.  That is a purely visual halo -- the negative margin cancels the
 * padding, so the box react-flow positions is the one ELK sized -- and the layout does not need
 * to know about it.
 */
const GROUP_PADDING = 32;

/**
 * How big an edge label is taken to be, for the layout engine.  Nothing measures it: it is
 * rendered after layout, at 1rem.  Generous, since a tight estimate puts the words back over nodes.
 */
const LABEL_CHARACTER_WIDTH = 9;
const LABEL_PADDING = 24;
const LABEL_HEIGHT = 28;

const edgeLabelSize = (edge: Edge): { width: number; height: number } | undefined => {
    const label = edge.data?.label;
    const text = typeof label === "string" ? label.trim() : typeof label === "number" ? String(label) : "";
    if (!text) {
        return undefined;
    }

    return { width: LABEL_PADDING + LABEL_CHARACTER_WIDTH * text.length, height: LABEL_HEIGHT };
};

/** Vertical offset that keeps a group's children clear of its heading. */
const GROUP_HEADING_OFFSET = 20;

/**
 * Height of the band title and its rule, which is what a link must not be drawn across.
 *
 * Deliberately smaller than the layout padding above: the rest of that padding is empty space
 * between the rule and the first row of nodes, and a link approaching a node from above has to
 * pass through it.  Treating the whole padding as solid left nowhere to enter a band from.
 */
const GROUP_HEADING_TEXT = 36;

/**
 * The rendered size of a group's heading text, measured off the DOM by `index.tsx` before layout.
 * Without a measurement (tests, no DOM) the width is estimated from the label: 1.5em capitals with
 * letter spacing run about 11px a character.
 */
type HeadingSize = { width: number; height: number };
type HeadingSizes = Map<string, HeadingSize>;

const TITLE_CHARACTER_WIDTH = 11;
const TITLE_PADDING = 16;

/** How wide a group's title is, measured or estimated; zero for a group with nothing to say. */
const headingWidth = (node: Node<any, string> | undefined, headings?: HeadingSizes): number => {
    if (!node) return 0;
    // A zero measurement is a heading that had not rendered yet as often as one that is empty.
    const measured = headings?.get(node.id);
    if (measured && measured.width > 0) return measured.width;

    const label = node.data?.label;
    const text = typeof label === "string" ? label.trim() : typeof label === "number" ? String(label) : "";
    return text ? TITLE_CHARACTER_WIDTH * text.length : 0;
};

/** How many times every routed link is routed again with all the others in place. */
const REROUTE_PASSES = 3;

/** Extra room at the top of a group so its heading does not sit on top of its children. */
const GROUP_HEADING_PADDING = GROUP_PADDING + GROUP_HEADING_OFFSET;

/**
 * The subset of `defaultEdgeOptions` that react-flow itself understands.
 *
 * `defaultEdgeOptions` also carries `color`, `width` and `excludeFromLayout`, which are ours and
 * not react-flow's.  v12's `EdgeWrapper` merges whatever it is given into every edge, so passing
 * the whole object would spread three unknown props onto every edge in the chart.  `mapInputData`
 * already sets `type` and `markerEnd` explicitly on each edge, so this only ever acts as a
 * backstop.
 */
const reactFlowEdgeDefaults: { type: string } = { type: defaultEdgeOptions.type };

/** The node shape this component puts into react-flow, named so the state hooks can be explicit. */
type FlowNode = Node<Record<string, any>, string>;

const DIRECTIONS = { LR: "RIGHT", RL: "LEFT", TB: "DOWN", BT: "UP" } as const;
const RANKERS = {
    "network-simplex": "NETWORK_SIMPLEX",
    "longest-path": "LONGEST_PATH",
    // ELK has no tight-tree layering. Dagre's tight-tree is its own initial feasible tree for
    // network simplex, so this is the closest equivalent rather than an arbitrary substitution --
    // but it means the value is indistinguishable from "network-simplex", hence the deprecation.
    "tight-tree": "NETWORK_SIMPLEX",
} as const;

/** Rankers that no longer mean anything of their own, and the reason why. */
const DEPRECATED_RANKERS: Record<string, string> = {
    "tight-tree": "the layout engine has no tight-tree equivalent, so it behaves exactly as "
        + "network-simplex. Switch to network-simplex; tight-tree will be removed.",
};

/** Warned about once per value, so a chart that re-lays out on every slider tick stays usable. */
const warned = new Set<string>();

const warnOnce = (key: string, message: string) => {
    if (warned.has(key)) {
        return;
    }

    warned.add(key);
    console.warn(`FlowChart: ${message}`);
};
const ALIGNMENTS = { UL: "LEFTUP", UR: "RIGHTUP", DL: "LEFTDOWN", DR: "RIGHTDOWN" } as const;

/**
 * Translate the documented (Dagre-flavoured) options into ELK's.
 *
 * The public names stay as they are: six of them are bound to live Autocompletes and Sliders in
 * the Operational Planning layout popover, so they are a runtime UI contract, not just a payload
 * shape.  `elkOptions` merges last as the escape hatch for anything not covered here.
 */
const toElkOptions = (options: ResolvedLayoutOptions): Record<string, string> => {
    const deprecation = DEPRECATED_RANKERS[options.ranker];
    if (deprecation) {
        warnOnce(`ranker:${options.ranker}`, `layoutOptions.ranker "${options.ranker}" is deprecated -- ${deprecation}`);
    }

    return ({
        "elk.algorithm": "layered",
        "elk.direction": DIRECTIONS[options.rankdir] ?? "RIGHT",
        "elk.edgeRouting": "ORTHOGONAL",
        // Lets edges between different groups be routed properly rather than clipped at the boundary.
        "elk.hierarchyHandling": "INCLUDE_CHILDREN",
        // Report edge geometry in ROOT coordinates.  ELK's default is to express each edge relative
        // to its lowest common ancestor, so in a grouped chart every route came back offset by its
        // group's origin -- and two groups whose children sat at the same relative offsets produced
        // IDENTICAL coordinates, stacking their edges on top of each other.  react-flow draws edges
        // in absolute flow space, so this has to be ROOT.
        "elk.json.edgeCoords": "ROOT",
        // elk.layered is deterministic, but pinning the seed makes that a contract: identical input
        // must give an identical diagram, which the crossing detection depends on.
        "elk.randomSeed": "1",
        "elk.layered.layering.strategy": RANKERS[options.ranker] ?? "NETWORK_SIMPLEX",
        // Break ties by the order the nodes were declared.  Crossing minimisation is still on, but
        // where it has a free choice -- which it does for every parallel branch off a fan-out that
        // rejoins at a fan-in -- it would otherwise pick arbitrarily, and a chart authored A-then-B
        // came back B-then-A.  Declaration order is what means something to a reader, and it is what
        // the previous engine did.
        "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
        "elk.layered.nodePlacement.strategy": "BRANDES_KOEPF",
        // `align: null` documents "centred", which is ELK's BALANCED.
        "elk.layered.nodePlacement.bk.fixedAlignment": ALIGNMENTS[options.align] ?? "BALANCED",
        "elk.spacing.nodeNode": String(options.nodesep),
        "elk.layered.spacing.nodeNodeBetweenLayers": String(options.ranksep),
        "elk.spacing.edgeEdge": String(options.edgesep),
        "elk.layered.spacing.edgeEdgeBetweenLayers": String(options.edgesep),
        // Labels sit on the edge, in the gap between layers, and that gap grows to hold them.
        "elk.edgeLabels.inline": "true",
        "elk.spacing.edgeLabel": "8",
        ...(options.elkOptions ?? {}),
    });
};

/**
 * Merge caller options over the defaults, deepening ONLY the two handle-mapping tables.
 *
 * A shallow spread replaced a whole mapping table with a partial one, silently dropping the other
 * seven directions (they then fell through to the `right`/`left` fallbacks in `getEdgeHandles`).
 * Deliberately not a generic recursive merge: `align: null` is a meaningful value meaning
 * "centred", and a recursive merge cannot tell it from absent.
 */
const mergeLayoutOptions = (overrides?: Partial<LayoutOptions>): ResolvedLayoutOptions => ({
    ...defaultLayoutOptions,
    ...overrides,
    fromHandleMappings: { ...defaultLayoutOptions.fromHandleMappings, ...overrides?.fromHandleMappings },
    toHandleMappings: { ...defaultLayoutOptions.toHandleMappings, ...overrides?.toHandleMappings },
});

/** id -> node, so the per-edge lookups below are O(1) rather than a scan of the whole chart. */
const nodesById = (nodes: Node<any, string>[]) => new Map(nodes.map(n => [n.id, n]));

const includeEdgeInLayout = (edge: Edge<any>, nodes: Map<string, Node<any, string>>, layoutOptions: LayoutOptions) => {
    const sourceNode = nodes.get(edge.source);
    const targetNode = nodes.get(edge.target);
    if (!sourceNode || !targetNode) {
        return false;
    }

    if (edge.excludeFromLayout === true) {
        return false;
    }

    return layoutOptions.includeInterGroupLinksInLayout || (sourceNode.parentId === targetNode.parentId);
};

type Point = { x: number; y: number };

/** An absolute, top-left-anchored box. The one coordinate space the layout code agrees on. */
type LayoutRect = { x: number; y: number; width: number; height: number };

/**
 * Pick which side of each node an edge should leave from and arrive at.
 *
 * Both rects must be ABSOLUTE and TOP-LEFT anchored.  This used to take react-flow nodes and
 * choose between `node.position` and `node.x`/`node.y` via a flag, which meant the fixed-position
 * branch supplied top-left, parent-relative coordinates while the Dagre branch supplied centre,
 * global ones -- to a comparison written for top-left.  Handle selection was therefore
 * systematically different, and differently wrong, between the two branches.  Callers now
 * normalise instead.
 */
/** The coarse "which way is the target" reading the mapping tables are keyed by. */
const directionKey = (source: LayoutRect, target: LayoutRect): keyof HandleMapping => {
    const xDirection = source.x + source.width < target.x ? "right" : Math.abs(source.x - target.x) < HANDLE_DEAD_BAND ? "straight" : "left";
    const yDirection = source.y + source.height < target.y ? "down" : Math.abs(source.y - target.y) < HANDLE_DEAD_BAND ? "straight" : "up";
    return `${xDirection}-${yDirection}` as keyof HandleMapping;
};

const getEdgeHandles = (source: LayoutRect, target: LayoutRect, edge: Edge<any>, layoutOptions: LayoutOptions) => {
    /* Reached for any edge the router did not route: a `manual` layout, which ELK cannot route at
       all, and -- in an automatic layout -- every link excluded from it, which by default means
       every inter-group link.  Where ELK DID produce a route the sides come from the route
       instead, which is better informed and automatically consistent with the drawn path. */
    if (!source || !target) {
        return {};
    }

    const key = directionKey(source, target);
    const sourceSide = asSide(edge.data?.positionStart)
        ?? asSide(layoutOptions?.fromHandleMappings?.[key])
        ?? "right";
    const targetSide = asSide(edge.data?.positionEnd)
        ?? asSide(layoutOptions?.toHandleMappings?.[key])
        ?? "left";

    return { sourceHandle: `source-${sourceSide}`, targetHandle: `target-${targetSide}` };
};

type ElkEdgeSection = {
    startPoint: Point;
    endPoint: Point;
    bendPoints?: Point[];
};

type ElkNode = {
    id: string;
    width?: number;
    height?: number;
    x?: number;
    y?: number;
    children?: ElkNode[];
    edges?: { id: string; sources: string[]; targets: string[]; sections?: ElkEdgeSection[]; labels?: { text: string; width: number; height: number }[] }[];
    ports?: { id: string; x?: number; y?: number; layoutOptions?: Record<string, string> }[];
    layoutOptions?: Record<string, string>;
};

/** Injectable so tests can drive layout deterministically, and out of order. */
type LayoutEngine = (graph: ElkNode) => Promise<ElkNode>;

let elkInstance: Promise<any> | undefined;

/**
 * The real engine.
 *
 * Imported dynamically: `elk.bundled.js` is ~2MB, and `vite.config.ts` only leaves it in its own
 * chunk for a dynamic import -- a static one would pull it into the main bundle.  The bundled
 * build runs on the main thread on purpose; the worker build loads its worker cross-origin, which
 * does not survive `ASSET_BASE_URL` pointing at the CDN.
 */
const elkLayout: LayoutEngine = async (graph) => {
    // A failed chunk fetch must not be cached: the rejected promise would make every later
    // layout fail for the lifetime of the page, long after the network recovered.
    elkInstance ??= import("elkjs/lib/elk.bundled.js").then((module: any) => {
        const Elk = module.default ?? module;
        return new Elk();
    }).catch((error) => {
        elkInstance = undefined;
        throw error;
    });

    return (await elkInstance).layout(graph);
};

const PORT_SIDES = { top: "NORTH", right: "EAST", bottom: "SOUTH", left: "WEST" } as const;

/** Port id for a given node side. Shared by every edge constrained to that side. */
const portId = (nodeId: string, side: HandlePosition) => `${nodeId}::${side}`;

/**
 * Which sides of which nodes an author has pinned, via a link's `positionStart` / `positionEnd`.
 *
 * These used to be applied AFTER layout, overriding a handle the geometry had already chosen --
 * so the router never knew about them and an edge could leave a node on the wrong side and
 * immediately double back. Declared as ELK ports they become a constraint the router honours.
 */
const requiredPorts = (edges: Edge[]) => {
    const sides = new Map<string, Set<HandlePosition>>();
    const add = (nodeId: string, side?: HandlePosition) => {
        const pinned = asSide(side);
        if (!pinned) return;
        const existing = sides.get(nodeId) ?? new Set<HandlePosition>();
        existing.add(pinned);
        sides.set(nodeId, existing);
    };

    edges.forEach((edge) => {
        add(edge.source, edge.data?.positionStart);
        add(edge.target, edge.data?.positionEnd);
    });

    return sides;
};

/** Flatten an ELK edge's sections into one polyline. Coordinates are absolute (edges sit at root). */
const routeOf = (elkEdge: any): Point[] | undefined => {
    const section = elkEdge?.sections?.[0];
    if (!section?.startPoint || !section?.endPoint) {
        return undefined;
    }

    return [section.startPoint, ...(section.bendPoints ?? []), section.endPoint]
        .map((p: any) => ({ x: p.x, y: p.y }));
};

/** Which side of a box a point sits on. Used to translate ELK's routing decision into a handle. */
const sideOfPoint = (point: Point, rect: LayoutRect): HandlePosition => {
    const distances: Record<HandlePosition, number> = {
        top: Math.abs(point.y - rect.y),
        bottom: Math.abs(point.y - (rect.y + rect.height)),
        left: Math.abs(point.x - rect.x),
        right: Math.abs(point.x - (rect.x + rect.width)),
    };

    return (Object.keys(distances) as HandlePosition[])
        .reduce((best, side) => (distances[side] < distances[best] ? side : best), "right");
};

/**
 * Pin where a link meets its target, so a fan-in reads as one arrow instead of several.
 *
 * For a link the ENGINE routed: the router seats its own routes.  Only the tail moves, and only
 * along the side it already arrives by, so the handle derived from the route still holds.  The
 * two points nearest the node move across; the run behind them simply changes length.
 *
 * Depth is deliberately left alone.  Pinning `along` already makes every member's final run share
 * one coordinate, so those runs are collinear -- one trunk, not several.  What is left pointing at
 * it are the perpendicular runs behind them, and each of those ENDS on the trunk rather than
 * crossing it, which `intersect` declines to treat as a crossing anyway.
 */
const pinArrival = (
    points: Point[],
    targetSide: HandlePosition,
    /** Where this fan has agreed to meet, along the side it arrives by. */
    seat: number
): Point[] => {
    // Simplified first, because the engine's own routes never are: `routeOf` hands back ELK's
    // points verbatim, and one duplicated or collinear point in the tail fails the shape test
    // below for a route that is really the right shape -- silently leaving that link behind while
    // the rest of its fan converges without it.
    const tail = simplify(points);
    if (tail.length < 3) {
        return points;
    }

    const along: "x" | "y" = targetSide === "top" || targetSide === "bottom" ? "x" : "y";

    const end = tail[tail.length - 1];
    const entry = tail[tail.length - 2];
    const before = tail[tail.length - 3];

    // A straight stub into the node, reached by a run square to it.  Anything else is a shape this
    // does not model.
    if (Math.abs(entry[along] - end[along]) > 0.5 || Math.abs(before[along] - entry[along]) <= 0.5) {
        return points;
    }

    const moved = tail.map(point => ({ ...point }));
    moved[moved.length - 1] = { ...end, [along]: seat };
    moved[moved.length - 2] = { ...entry, [along]: seat };

    return simplify(moved);
};

/** Inward normal of a side: from the box edge towards the node's middle. */
const INWARD: Record<HandlePosition, Point> = {
    top: { x: 0, y: 1 },
    bottom: { x: 0, y: -1 },
    left: { x: 1, y: 0 },
    right: { x: -1, y: 0 },
};

/**
 * Move both ends of a route onto the outline of the shape they touch.
 *
 * The layout engine routes to a node's BOX, but the shape is drawn inside it -- barely for a
 * rectangle, by a fifth of its width for a triangle's face.  Left alone, an arrowhead floats
 * clear of every pointed shape, which is the behaviour the handle offsets used to provide before
 * routes replaced handle-to-handle paths.
 *
 * Both sides are perpendicular to their final segment, so moving along the side's inward normal
 * keeps the route axis-aligned.
 */
const seatEndpoints = (
    points: Point[],
    source: LayoutRect,
    target: LayoutRect,
    sourceSide: HandlePosition,
    targetSide: HandlePosition,
    sourceType?: string,
    targetType?: string
): Point[] => {
    if (points.length < 2) {
        return points;
    }

    const seat = (point: Point, rect: LayoutRect, side: HandlePosition, variant?: string) => {
        // Where along the side this link meets it, as a fraction of the drawn shape. A sloping
        // face is at a different depth at every height, so measuring at the middle put every
        // link arriving at a triangle in the same place -- the ones above the middle stopping
        // short in empty space, the ones below overshooting inside the shape.
        const box = {
            x: rect.x + NODE_CONTENT_PADDING,
            y: rect.y + NODE_CONTENT_PADDING,
            width: Math.max(1, rect.width - 2 * NODE_CONTENT_PADDING),
            height: Math.max(1, rect.height - 2 * NODE_CONTENT_PADDING),
        };
        const along = side === "left" || side === "right"
            ? (point.y - box.y) / box.height
            : (point.x - box.x) / box.width;

        const inset = shapeInset(variant, side, rect.width, rect.height, along);
        return {
            x: point.x + INWARD[side].x * inset,
            y: point.y + INWARD[side].y * inset,
        };
    };

    const out = points.map(point => ({ ...point }));
    out[0] = seat(out[0], source, sourceSide, sourceType);
    out[out.length - 1] = seat(out[out.length - 1], target, targetSide, targetType);
    return out;
};

/**
 * Lay the graph out one level at a time, innermost first.
 *
 * ELK can lay out a nested graph in a single pass, but its crossing minimisation then chooses the
 * order of a group's children freely and there is no way to tell it otherwise: the model-order
 * option does not reach children of a compound node, declaring it ON the compound node makes
 * elkjs throw, and the per-node position constraints are ignored there.  Declaration order is what
 * a reader expects and what the previous engine produced, so each level is laid out as its own
 * FLAT graph, where the option does work.
 *
 * Sizes flow upwards: a group is laid out first, and its parent then treats it as a fixed box.
 * That keeps the clustering the hierarchy exists for.
 */
const layoutByLevel = async (
    nodes: Node<any, string>[],
    edges: Edge[],
    dimensions: Map<string, { width: number; height: number }>,
    options: ResolvedLayoutOptions,
    engine: LayoutEngine,
    headings?: HeadingSizes
) => {
    const childrenOf = new Map<string | undefined, Node<any, string>[]>();
    for (const node of nodes) {
        childrenOf.set(node.parentId, [...(childrenOf.get(node.parentId) ?? []), node]);
    }

    const parents = parentOf(nodes);
    /** Which child of `level` an endpoint belongs to, if any. */
    const ownerAt = (id: string, level: string | undefined) => {
        let current: string | undefined = id;
        while (current != null && parents.get(current) !== level) {
            current = parents.get(current);
        }
        return current;
    };

    const rects = new Map<string, LayoutRect>();
    const routes = new Map<string, { points: Point[]; level: string | undefined }>();
    const sizes = new Map<string, { width: number; height: number }>();
    /** Levels whose children were stacked rather than laid out, so their groups can fill them. */
    const stacked = new Set<string | undefined>();

    const byId = nodesById(nodes);
    const laidOut = edges.filter(e => includeEdgeInLayout(e, byId, options));
    // Keyed by node id and read back per child below, so one pass over the laid-out edges serves
    // every level.
    const ports = requiredPorts(laidOut);

    const layoutLevel = async (level: string | undefined): Promise<{ width: number; height: number }> => {
        const children = childrenOf.get(level) ?? [];

        // Innermost first, so a group is a known size by the time its parent is laid out.
        for (const child of children) {
            if ((childrenOf.get(child.id) ?? []).length) {
                sizes.set(child.id, await layoutLevel(child.id));
            }
        }

        // A group's padding leaves room for its heading; the root just gets a margin.
        const half = GROUP_PADDING / 2;
        const top = level == null ? half : GROUP_HEADING_PADDING;

        const levelEdges = laidOut
            .map(edge => ({ edge, from: ownerAt(edge.source, level), to: ownerAt(edge.target, level) }))
            .filter(({ from, to }) => from != null && to != null && from !== to);
        const levelEdgeById = new Map(levelEdges.map(entry => [entry.edge.id, entry]));

        const laid = await engine({
            id: level ?? "root",
            layoutOptions: {
                ...toElkOptions(options),
                "elk.padding": `[top=${top},left=${half},bottom=${half},right=${half}]`,
            },
            children: children.map((child) => {
                const sides = [...(ports.get(child.id) ?? [])];
                return {
                    id: child.id,
                    ...(sizes.get(child.id) ?? dimensions.get(child.id)),
                    ...(sides.length ? {
                        layoutOptions: { "elk.portConstraints": "FIXED_SIDE" },
                        ports: sides.map(side => ({
                            id: portId(child.id, side),
                            layoutOptions: { "elk.port.side": PORT_SIDES[side] },
                        })),
                    } : {}),
                };
            }),
            edges: levelEdges.map(({ edge, from, to }) => {
                // Through `asSide`, because that is what decided whether the port exists at all:
                // `requiredPorts` declares one only for a recognised side, so an authored `"Left"`
                // is truthy here but has nothing to point at -- and an edge referencing a port ELK
                // was never given is fatal to the layout, not to that one link.
                const start = asSide(edge.data?.positionStart);
                const end = asSide(edge.data?.positionEnd);
                const label = edgeLabelSize(edge);

                return {
                    id: edge.id,
                    sources: [start && from === edge.source ? portId(from!, start) : from!],
                    targets: [end && to === edge.target ? portId(to!, end) : to!],
                    // Only where the edge joins two of this level's own children: on a container
                    // edge the label belongs to the level below.
                    ...(label && from === edge.source && to === edge.target
                        ? { labels: [{ text: String(edge.data?.label), ...label }] }
                        : {}),
                };
            }),
        });

        const boxes = new Map((laid.children ?? []).map(child => [child.id, {
            x: child.x ?? 0, y: child.y ?? 0,
            width: child.width ?? 0, height: child.height ?? 0,
        } as LayoutRect]));
        const stackedSize = levelEdges.length ? undefined : stackSiblings(children, boxes, options, { top, side: half });
        if (stackedSize) {
            stacked.add(level);
        }
        const size = stackedSize ?? { width: laid.width ?? 0, height: laid.height ?? 0 };

        // Never narrower than its heading, or the title overflows its rule.
        size.width = Math.max(size.width, headingWidth(byId.get(level!), headings) + GROUP_PADDING);

        for (const [id, box] of boxes) {
            rects.set(id, box);
        }

        for (const elkEdge of laid.edges ?? []) {
            const match = levelEdgeById.get(elkEdge.id);
            // Only keep a route whose real endpoints ARE this level's children. An edge between
            // two descendants was laid out between their containers, so its route would join the
            // wrong boxes -- better to route one that reaches the actual nodes.
            if (!match || match.from !== match.edge.source || match.to !== match.edge.target) continue;

            const points = routeOf(elkEdge);
            if (points) {
                routes.set(elkEdge.id, { points, level });
            }
        }

        return size;
    };

    await layoutLevel(undefined);
    return { rects, routes, stacked };
};

/**
 * Give every group at a stacked level its parent's inner width, so room a parent gains from ITS
 * siblings reaches down.  Parents precede children in the node list, so one pass cascades.
 */
const fillStackedGroups = (
    nodes: Node<any, string>[],
    rects: Map<string, LayoutRect>,
    stacked: Set<string | undefined>,
    options: ResolvedLayoutOptions
) => {
    const flow: "x" | "y" = options.rankdir === "TB" || options.rankdir === "BT" ? "y" : "x";
    const extent = flow === "x" ? "width" : "height";
    // The same padding `stackSiblings` used: the heading's room where the flow runs down the page.
    const start = flow === "y" ? GROUP_HEADING_PADDING : GROUP_PADDING / 2;

    for (const node of nodes) {
        if (node.type !== "group" || node.parentId == null || !stacked.has(node.parentId)) continue;
        const own = rects.get(node.id);
        const parent = rects.get(node.parentId);
        if (!own || !parent) continue;

        rects.set(node.id, { ...own, [flow]: start, [extent]: Math.max(own[extent], parent[extent] - start - GROUP_PADDING / 2) });
    }
};

/**
 * Positioned nodes, and edges carrying the geometry the renderer draws.
 *
 * Written out rather than inferred, and that is load-bearing: the inferred type reaches into
 * `csstype` through the nodes' `style`, which `vite-plugin-dts` cannot name from the published
 * tree (TS2742).  It gave up on the whole module, so `layout.d.ts` was never emitted -- while
 * `index.d.ts` and `shapeGeometry.d.ts` both `import ... from './layout'`.  The published
 * `@qerent/lib` therefore shipped FlowChart declarations that could not resolve.
 */
type LayoutResult = { nodes: FlowNode[]; edges: Edge[] };

const getLayoutedElements = async (
    inputNodes: Node<any, string>[],
    edges: Edge[],
    options: ResolvedLayoutOptions,
    engine: LayoutEngine = elkLayout,
    /** Measured heading text sizes by group id; estimated from the labels where absent. */
    headings?: HeadingSizes
): Promise<LayoutResult> => {
    // Whether the author placed the nodes themselves.  The implicit form is deprecated but kept:
    // there is no inventory of consumers beyond the templates we know about.
    const useFixedNodePositions = options.layout
        ? options.layout === "manual"
        : inputNodes.some(n => n.data?.hasAuthoredPosition ?? (n.position.x || n.position.y));

    // A node that was never mounted (hidden) has no measurement at all.  Feed it 0x0 rather than
    // undefined: the engine turns undefined into NaN positions that nothing downstream detects.
    const dimensions = new Map<string, { width: number; height: number }>(
        inputNodes.map(node => [node.id, {
            width: Number.isFinite(nodeWidth(node)) ? nodeWidth(node) : 0,
            height: Number.isFinite(nodeHeight(node)) ? nodeHeight(node) : 0,
        }])
    );

    /** Laid-out geometry, parent-relative and top-left anchored, keyed by node id. */
    let layoutNodes: Map<string, LayoutRect>;
    /** Routes as the engine produced them: relative to the level they were laid out at. */
    let levelRoutes = new Map<string, { points: Point[]; level: string | undefined }>();

    if (useFixedNodePositions) {
        layoutNodes = new Map();

        // Indexed once rather than re-scanned per node.  `layoutByLevel` builds the same map for
        // the automatic branch; this one was scanning the whole chart for every node it visited,
        // which is the same O(n^2) the lookup map exists to avoid.  Hidden children are left out
        // here, because the only thing this index is used for is sizing a group around what is
        // actually drawn inside it.
        const visibleChildrenOf = new Map<string, Node<any, string>[]>();
        for (const node of inputNodes) {
            if (node.hidden || node.parentId == null) {
                continue;
            }

            const siblings = visibleChildrenOf.get(node.parentId);
            if (siblings) {
                siblings.push(node);
            } else {
                visibleChildrenOf.set(node.parentId, [node]);
            }
        }

        inputNodes.forEach((node) => {
            const children = visibleChildrenOf.get(node.id) ?? [];
            const { width, height } = dimensions.get(node.id);

            if (!children.length) {
                layoutNodes.set(node.id, { x: node.position.x, y: node.position.y, width, height });
                return;
            }

            // Children are positioned relative to this group, so the box spans from its origin out
            // to the furthest child edge.  Measuring `maxX - minX` instead dropped the offset, and
            // any group whose children did not start at 0 overflowed.
            const maxX = Math.max(...children.map(n => n.position.x + dimensions.get(n.id).width));
            const maxY = Math.max(...children.map(n => n.position.y + dimensions.get(n.id).height));

            layoutNodes.set(node.id, {
                x: node.position.x,
                y: node.position.y,
                width: maxX + GROUP_PADDING,
                height: maxY + GROUP_PADDING,
            });
        });
    } else {
        // ELK returns children parent-relative and top-left anchored, which is exactly react-flow's
        // own convention -- so the centre-to-top-left shift and the parent rebase that Dagre
        // needed both disappear here.
        const levelled = await layoutByLevel(inputNodes, edges, dimensions, options, engine, headings);
        layoutNodes = levelled.rects;
        levelRoutes = levelled.routes;
        fillStackedGroups(inputNodes, layoutNodes, levelled.stacked, options);
    }

    const parents = parentOf(inputNodes);

    // Each level's routes are relative to that level's origin, so lift them into absolute space
    // using the level's own resolved position.
    const routes = new Map<string, Point[]>();
    for (const [id, { points, level }] of levelRoutes) {
        const origin = level == null ? { x: 0, y: 0 } : absoluteRect(level, layoutNodes, parents);
        if (!origin) continue;

        routes.set(id, points.map(point => ({ x: point.x + origin.x, y: point.y + origin.y })));
    }

    // A node moved here has no engine route of its own, so nothing lifted above is left behind.
    if (!useFixedNodePositions) {
        attractUnlinkedNodes(inputNodes, edges, layoutNodes, routes, parents, options);
    }

    return {
        nodes: inputNodes.map((node) => {
            const laid = layoutNodes.get(node.id) ?? { x: 0, y: 0, ...dimensions.get(node.id) };
            return {
                ...node,
                position: { x: laid.x, y: laid.y },
                // Dimensions go on `style`, never top level: in v12 a top-level width or height
                // marks a node author-sized and stops react-flow re-measuring it.
                style: { ...node.style, width: laid.width, height: laid.height },
            };
        }),
        edges: attachEdgeGeometry(inputNodes, edges, layoutNodes, routes, options, undefined, headings, useFixedNodePositions),
    };
};

/**
 * Give every edge its final geometry: which side it leaves and enters by, the polyline it follows,
 * and the crossings it is responsible for indicating.
 *
 * Split out of `getLayoutedElements` so that re-routing after a drag can reuse it verbatim rather
 * than growing a second, subtly different router.  Everything it needs is already resolved: node
 * boxes in `layoutNodes` (parent-relative, as both branches produce them) and whatever routes the
 * engine managed to produce, in absolute coordinates.  Edges the engine did not route are routed
 * here (`router.ts`), so the renderer and the crossing pass can treat them all alike.
 */
const attachEdgeGeometry = (
    inputNodes: Node<any, string>[],
    edges: Edge[],
    layoutNodes: Map<string, LayoutRect>,
    /** Absolute routes, keyed by edge id. Absent for any edge the engine did not route. */
    routes: Map<string, Point[]>,
    options: ResolvedLayoutOptions,
    /**
     * The nodes that just moved, when this is a re-route rather than a layout.
     *
     * Present only on the drag path.  Absent -- as it is from `getLayoutedElements` -- every edge
     * is routed from scratch, which is what a fresh layout wants.
     */
    movedNodes?: Set<string>,
    headings?: HeadingSizes,
    /** A manual layout: the deprecated handle-mapping tables name each link's preferred sides. */
    manual = false
): Edge[] => {
    const parents = parentOf(inputNodes);
    const nodeTypes = new Map(inputNodes.map(n => [n.id, n.type as string | undefined]));
    const byNodeId = nodesById(inputNodes);

    /** Leaf nodes: what a route must not be drawn underneath. */
    const solid: Obstacle[] = [];
    /**
     * Group heading strips: what a route may cross at a price.  The rest of a group's box is no
     * obstacle -- links have to get inside it -- but the gaps between groups are where inter-band
     * runs belong, so the boxes go in as `channels`.
     */
    const soft: SoftRect[] = [];
    const channels: LayoutRect[] = [];
    /**
     * Nodes that paint OVER the edge layer, so a crossing indicator behind one would be wasted.
     *
     * Groups are excluded deliberately: `.react-flow__node-group` is `z-index: 0` against
     * `.react-flow__edges` at `z-index: 1` (FlowChart.scss), so edges are drawn on top of a band,
     * not under it.  Passing band boxes here swallowed every crossing inside a band -- which is
     * precisely where the crossings on a plant diagram are.
     */
    const occluding: LayoutRect[] = [];
    /** Nodes react-flow is not rendering, and so neither are the edges attached to them. */
    const hidden = new Set<string>();
    let chart: LayoutRect | undefined;

    for (const node of inputNodes) {
        if (node.hidden) {
            hidden.add(node.id);
            continue;
        }

        const rect = absoluteRect(node.id, layoutNodes, parents);
        if (!rect) continue;

        chart = chart ? {
            x: Math.min(chart.x, rect.x),
            y: Math.min(chart.y, rect.y),
            width: Math.max(chart.x + chart.width, rect.x + rect.width) - Math.min(chart.x, rect.x),
            height: Math.max(chart.y + chart.height, rect.y + rect.height) - Math.min(chart.y, rect.y),
        } : { ...rect };

        if (node.type === "group") {
            // Only the title strip, not the whole band.
            soft.push({ id: node.id, x: rect.x, y: rect.y, width: rect.width, height: GROUP_HEADING_TEXT });
            channels.push(rect);
        } else {
            solid.push({ id: node.id, ...rect });
            occluding.push(rect);
        }
    }

    // Titles are SOLID: a link through a band's name is unreadable.  Measured width where the DOM
    // gave one, estimated from the label otherwise.
    for (const heading of soft.slice()) {
        const width = headingWidth(byNodeId.get(heading.id), headings);
        if (!width) continue;

        const measured = headings?.get(heading.id);
        solid.push({
            id: `${heading.id}:title`,
            x: heading.x,
            y: heading.y,
            width: Math.min(heading.width, TITLE_PADDING + width),
            height: Math.max(GROUP_HEADING_TEXT, (measured?.height ?? 0) + TITLE_PADDING / 2),
        });
    }

    const rectOf = (id: string) => absoluteRect(id, layoutNodes, parents);
    const edgeById = new Map(edges.map(edge => [edge.id, edge]));

    /** What is already drawn.  The engine's routes are seeded first; routed links follow in declaration order. */
    const occupancy = new Occupancy();
    const claim = (edge: Edge, points: Point[]) => {
        occupancy.claimRoute(edge.id, points);
        const source = rectOf(edge.source);
        const target = rectOf(edge.target);
        if (points.length < 2 || !source || !target) return;

        const first = points[0];
        const last = points[points.length - 1];
        const sourceSide = sideOfPoint(first, source);
        const targetSide = sideOfPoint(last, target);
        occupancy.claimPort(edge.id, edge.source, sourceSide, first[alongAxis(sourceSide)]);
        occupancy.claimPort(edge.id, edge.target, targetSide, last[alongAxis(targetSide)]);
    };

    /** Whether a route can be searched for at all: a hidden node has no size, a direct link no drawn route. */
    const searchable = (edge: Edge) =>
        !hidden.has(edge.source) && !hidden.has(edge.target) && edge.data?.routing !== "direct";

    // Only what is drawn is claimed: the engine routes edges into hidden nodes too.
    for (const [id, points] of routes) {
        const edge = edgeById.get(id);
        if (edge && searchable(edge)) claim(edge, points);
    }

    /**
     * Which targets a fan-in actually arrives at, so merging only fires where there is a fan.
     *
     * A self-link is not a fan-in, and a `direct` link is not drawn along its route at all, so
     * neither should make a node look busier than it is.
     */
    const inbound = new Map<string, number>();
    if (options.mergeLinks) {
        for (const edge of edges) {
            if (edge.source === edge.target || !searchable(edge)) continue;
            inbound.set(edge.target, (inbound.get(edge.target) ?? 0) + 1);
        }
    }

    /**
     * Whether this link should be pinned to its target's shared arrival rather than given its own.
     *
     * An authored `positionEnd` is excluded: it already puts every link carrying it on one fixed
     * port, and the rule elsewhere in this file is that it wins outright -- a router that quietly
     * moved it would be worse than one that never offered it.
     */
    const merges = (edge: Edge) =>
        (inbound.get(edge.target) ?? 0) >= 2
        && edge.source !== edge.target
        && searchable(edge)
        && !asSide(edge.data?.positionEnd);

    /**
     * Where each fan has agreed to meet: the side, keyed by node, and the point along it, keyed by
     * node and side.  Both are decided by the first member routed.  A fan cannot span two sides.
     */
    const mergedSides = new Map<string, HandlePosition>();
    const mergedSeats = new Map<string, number>();
    const seatKey = (edge: Edge, side: HandlePosition) => `${edge.target}:${side}`;

    /**
     * Where a fan meets: the middle of the side, which is the one point a member drawn as a single
     * straight run can reach, unless a link outside the fan already sits there -- then where the
     * first member arrived.  The fan's own claims do not count against it.
     */
    const seatFor = (edge: Edge, target: LayoutRect, side: HandlePosition, first: Point[]): number => {
        const middle = sideMidpoint(target, side)[alongAxis(side)];
        const others = occupancy.portClaims(edge.target, side, id => edgeById.get(id)?.target === edge.target);
        const taken = others.some(claim => Math.abs(claim - middle) < ARROWHEAD_CLEARANCE);
        return taken ? first[first.length - 1][alongAxis(side)] : middle;
    };

    /** The facing side first, then the rest in a fixed order, so ties break the same on every run. */
    const orderedSides = (preferred: HandlePosition): HandlePosition[] =>
        [preferred, ...SIDES.filter(side => side !== preferred)];

    /**
     * Edges a drag leaves alone: neither end moved and the route is still clear of every moved
     * node.  Handed back untouched, so `seatEndpoints` does not inset them a second time.  Claimed
     * up front so a re-routed link earlier in the array sees a preserved one later in it.
     */
    const preserved = new Set<string>();
    if (movedNodes) {
        const movedObstacles = solid.filter(o => movedNodes.has(o.id));
        for (const edge of edges) {
            const existing = (edge.data?.route as Point[] | undefined) ?? [];
            if (!movedNodes.has(edge.source)
                && !movedNodes.has(edge.target)
                && existing.length >= 2
                && routeIsClear(movedObstacles, existing)) {
                preserved.add(edge.id);
                claim(edge, existing);

                // A preserved fan member keeps the seat the fan agreed, so a re-routed member
                // still meets it there.
                const target = rectOf(edge.target);
                if (merges(edge) && target) {
                    const last = existing[existing.length - 1];
                    const side = sideOfPoint(last, target);
                    mergedSides.set(edge.target, side);
                    mergedSeats.set(seatKey(edge, side), last[alongAxis(side)]);
                }
            }
        }
    }

    type Routed = { route: Point[]; sourceSide: HandlePosition; targetSide: HandlePosition; searched: boolean };
    const results = new Map<string, Routed>();

    /**
     * Route one edge the engine did not, against everything currently claimed.  The facing side is
     * a mild preference; an authored pin or a fan's agreed side is the only side offered.
     */
    /**
     * The sides a manual layout's mapping tables name for this link, where the caller changed them
     * from the defaults.  Such an entry is authored intent and pins the side like `positionStart`;
     * a default entry is left to the router.
     */
    const tabledSides = (source: LayoutRect, target: LayoutRect): [HandlePosition | undefined, HandlePosition | undefined] => {
        if (!manual) return [undefined, undefined];
        const key = directionKey(source, target);
        const from = options.fromHandleMappings[key];
        const to = options.toHandleMappings[key];
        return [
            from !== defaultLayoutOptions.fromHandleMappings[key] ? asSide(from) : undefined,
            to !== defaultLayoutOptions.toHandleMappings[key] ? asSide(to) : undefined,
        ];
    };

    const routeOne = (edge: Edge, source: LayoutRect, target: LayoutRect): Routed => {
        const [preferredSource, preferredTarget] = facingSides(source, target);
        const [tabledStart, tabledEnd] = tabledSides(source, target);
        const pinnedStart = asSide(edge.data?.positionStart) ?? tabledStart;
        const pinnedEnd = asSide(edge.data?.positionEnd) ?? tabledEnd;
        const agreedSide = merges(edge) ? mergedSides.get(edge.target) : undefined;
        const targetSides = pinnedEnd ? [pinnedEnd] : agreedSide ? [agreedSide] : orderedSides(preferredTarget);
        const fixedAlong = agreedSide ? mergedSeats.get(seatKey(edge, agreedSide)) : undefined;

        const ends: PortRequest[] = [
            { nodeId: edge.source, rect: source, sides: pinnedStart ? [pinnedStart] : orderedSides(preferredSource) },
            { nodeId: edge.target, rect: target, sides: targetSides, fixedAlong },
        ];

        if (!searchable(edge)) {
            // Not drawn, or sizeless: a plain route for the renderer to fall back on, claiming nothing.
            const plain = routeEdge({
                source: { ...ends[0], rect: { ...source, width: 0, height: 0 } },
                target: ends[1],
                solid: [], soft: [], channels: [], occupancy: new Occupancy(),
                edgesep: options.edgesep, chart: { x: 0, y: 0, width: 0, height: 0 },
            });
            return { route: plain.points, sourceSide: plain.sourceSide, targetSide: plain.targetSide, searched: false };
        }

        const search = (from: PortRequest, to: PortRequest) => routeEdge({
            source: from,
            target: to,
            solid: solid.filter(o => o.id !== edge.source && o.id !== edge.target),
            soft,
            channels,
            occupancy,
            edgesep: options.edgesep,
            chart: chart ?? { x: 0, y: 0, width: 0, height: 0 },
        });

        let result: RouteResult = search(ends[0], ends[1]);

        // An authored side holds while it is cheap.  Where it forces a crossing or a long way round,
        // the free route is used and the author is told: pins outlive the arrangement they suited.
        if (pinnedStart || pinnedEnd) {
            const free = search(
                { ...ends[0], sides: orderedSides(preferredSource) },
                { ...ends[1], sides: agreedSide ? [agreedSide] : orderedSides(preferredTarget) },
            );
            if (result.cost - free.cost > PIN_OVERRIDE) {
                const pins = [
                    pinnedStart && (asSide(edge.data?.positionStart) ? `positionStart "${pinnedStart}"` : `fromHandleMappings "${pinnedStart}"`),
                    pinnedEnd && (asSide(edge.data?.positionEnd) ? `positionEnd "${pinnedEnd}"` : `toHandleMappings "${pinnedEnd}"`),
                ].filter(Boolean).join(" and ");
                warnOnce(`pin:${edge.id}:${pins}`, `link "${edge.id}" pins ${pins}, but routing it there costs a crossing or a long detour. `
                    + `It was routed ${free.sourceSide} -> ${free.targetSide} instead; remove the pin or move the nodes to keep it.`);
                result = free;
            }
        }

        if (merges(edge)) {
            // First member routed settles the side and the seat for the rest of its fan.
            mergedSides.set(edge.target, result.targetSide);
            const key = seatKey(edge, result.targetSide);
            if (!mergedSeats.has(key)) {
                mergedSeats.set(key, result.points[result.points.length - 1][alongAxis(result.targetSide)]);
            }
        }
        claim(edge, result.points);
        return { route: result.points, sourceSide: result.sourceSide, targetSide: result.targetSide, searched: true };
    };

    // First pass, in declaration order: the engine's routes as they are, everything else routed
    // against what has been claimed so far.
    for (const edge of edges) {
        const source = rectOf(edge.source);
        const target = rectOf(edge.target);
        if (!source || !target || preserved.has(edge.id)) continue;

        const routed = routes.get(edge.id);
        if (!routed) {
            results.set(edge.id, routeOne(edge, source, target));
            continue;
        }

        // Prefer the engine's own decision. Where ELK produced a route, the side each end
        // attaches to is wherever the route actually starts and finishes -- which beats
        // guessing from node centres, and is automatically consistent with the drawn path.
        // Straightened like every other route, or a short jog beside a port pulls the chart's
        // corner radius down.
        let route = straighten(routed, solid.filter(o => o.id !== edge.source && o.id !== edge.target));
        const sourceSide = sideOfPoint(routed[0], source);
        const targetSide = sideOfPoint(routed[routed.length - 1], target);

        if (merges(edge)) {
            // A fan-in inside one band is routed by ELK, so the option has to reach into engine
            // routes too.  Only the tail moves, along the side it already arrives by, so the
            // handle above still holds.
            mergedSides.set(edge.target, targetSide);
            const key = seatKey(edge, targetSide);
            mergedSeats.set(key, mergedSeats.get(key) ?? seatFor(edge, target, targetSide, routed));
            const seat = mergedSeats.get(key)!;
            route = pinArrival(route, targetSide, seat);
            occupancy.release(edge.id);
            claim(edge, route);
        }
        results.set(edge.id, { route, sourceSide, targetSide, searched: false });
    }

    // Further passes: each routed link again, with every other link in place, so an earlier link
    // can move over for a later one it knew nothing about.  Settles within a few passes.
    const same = (a: Point[], b: Point[]) =>
        a.length === b.length && a.every((point, i) => Math.abs(point.x - b[i].x) < 0.5 && Math.abs(point.y - b[i].y) < 0.5);
    for (let pass = 0; pass < REROUTE_PASSES; pass++) {
        let changed = false;
        for (const edge of edges) {
            const current = results.get(edge.id);
            if (!current?.searched) continue;
            const source = rectOf(edge.source)!;
            const target = rectOf(edge.target)!;

            occupancy.release(edge.id);
            const next = routeOne(edge, source, target);
            results.set(edge.id, next);
            changed ||= !same(current.route, next.route);
        }
        if (!changed) break;
    }

    // One corner treatment for the whole chart, decided here because only here are all routes known.
    const drawn = edges.filter(searchable).map(edge => results.get(edge.id)?.route).filter((r): r is Point[] => !!r);
    const corner = options.corners === "mixed"
        ? {}
        : { corners: options.corners, cornerRadius: options.corners === "rounded" ? chartRadius(drawn) : 0 };

    return withCrossings(edges.map((edge) => {
        const routed = results.get(edge.id);
        const source = rectOf(edge.source);
        const target = rectOf(edge.target);
        if (!routed || !source || !target) {
            return edge;
        }

        return {
            ...edge,
            sourceHandle: `source-${routed.sourceSide}`,
            targetHandle: `target-${routed.targetSide}`,
            data: {
                ...edge.data,
                ...corner,
                route: seatEndpoints(
                    routed.route, source, target, routed.sourceSide, routed.targetSide,
                    nodeTypes.get(edge.source), nodeTypes.get(edge.target)
                ),
            },
        };
    }), occluding, hidden);
};

/**
 * Pull a node with no links inside its own group along its band towards the nodes it links to.
 *
 * ELK lays a group out from the links INSIDE it, so such a node is an unconnected component and is
 * parked wherever the components pack, however far from its links.  It moves to the middle of its
 * partners, into the nearest place clear of its neighbours, and stays in its group.  Nodes ELK
 * placed from their own links keep their place, and no engine route touches a node that moves.
 */
const attractUnlinkedNodes = (
    nodes: Node<any, string>[],
    edges: Edge[],
    rects: Map<string, LayoutRect>,
    /** The engine's routes, absolute: a moved node must not be parked on one. */
    routes: Map<string, Point[]>,
    parents: Map<string, string | undefined>,
    options: ResolvedLayoutOptions
) => {
    const flow: "x" | "y" = options.rankdir === "TB" || options.rankdir === "BT" ? "y" : "x";
    const cross: "x" | "y" = flow === "x" ? "y" : "x";
    const extent = flow === "x" ? "width" : "height";
    const crossExtent = cross === "x" ? "width" : "height";
    const byId = nodesById(nodes);
    const hidden = new Set(nodes.filter(n => n.hidden).map(n => n.id));

    const linkedInside = new Set<string>();
    const partners = new Map<string, string[]>();
    for (const edge of edges) {
        if (edge.source === edge.target || hidden.has(edge.source) || hidden.has(edge.target)) continue;
        if (includeEdgeInLayout(edge, byId, options)) {
            linkedInside.add(edge.source);
            linkedInside.add(edge.target);
        }
        partners.set(edge.source, [...(partners.get(edge.source) ?? []), edge.target]);
        partners.set(edge.target, [...(partners.get(edge.target) ?? []), edge.source]);
    }

    const absolute = (id: string) => absoluteRect(id, rects, parents);
    const runs = [...routes.values()].flatMap(points => points.slice(1).map((to, i) => runBounds(points[i], to)));

    for (const node of nodes) {
        if (node.hidden || node.type === "group" || node.parentId == null || linkedInside.has(node.id)) continue;
        const others = (partners.get(node.id) ?? []).map(absolute).filter((r): r is LayoutRect => !!r);
        const rect = absolute(node.id);
        const parent = absolute(node.parentId);
        if (!others.length || !rect || !parent) continue;

        const wanted = others.reduce((sum, o) => sum + o[flow] + o[extent] / 2, 0) / others.length - rect[extent] / 2;
        const low = parent[flow] + GROUP_PADDING / 2;
        const high = parent[flow] + parent[extent] - GROUP_PADDING / 2 - rect[extent];
        if (high < low) continue;

        // The neighbours it could land on: visible leaves of the same group sharing its row.
        const siblings = nodes
            .filter(n => n.parentId === node.parentId && n.id !== node.id && !n.hidden && n.type !== "group")
            .map(n => absolute(n.id))
            .filter((s): s is LayoutRect => !!s
                && s[cross] < rect[cross] + rect[crossExtent] && s[cross] + s[crossExtent] > rect[cross]);
        const clear = (at: number) =>
            !siblings.some(s => at < s[flow] + s[extent] + options.nodesep - 0.5 && at + rect[extent] + options.nodesep - 0.5 > s[flow])
            && !runs.some(run => runHits(run, { ...rect, [flow]: at }));
        const clamp = (at: number) => Math.min(high, Math.max(low, at));

        // Where it wants to be, else the nearest spot beside a neighbour that is in the way.
        const candidates = [wanted, ...siblings.flatMap(s => [s[flow] + s[extent] + options.nodesep, s[flow] - options.nodesep - rect[extent]])]
            .map(clamp)
            .filter(clear);
        if (!candidates.length) continue;

        const best = candidates.reduce((a, b) => (Math.abs(b - wanted) < Math.abs(a - wanted) ? b : a));
        const own = rects.get(node.id)!;
        rects.set(node.id, { ...own, [flow]: own[flow] + (best - rect[flow]) });
    }
};

/**
 * The side of each box that faces the other: the natural side for a link between them to use.
 *
 * Decided by the larger of the two centre offsets, so two nodes side by side connect left-to-right
 * and two stacked connect top-to-bottom.  The two are always opposite each other.
 */
const facingSides = (from: LayoutRect, to: LayoutRect): [HandlePosition, HandlePosition] => {
    const dx = (to.x + to.width / 2) - (from.x + from.width / 2);
    const dy = (to.y + to.height / 2) - (from.y + from.height / 2);

    if (Math.abs(dx) >= Math.abs(dy)) {
        return dx >= 0 ? ["right", "left"] : ["left", "right"];
    }

    return dy >= 0 ? ["bottom", "top"] : ["top", "bottom"];
};

/**
 * Recompute every edge's geometry against the nodes' CURRENT positions.
 *
 * For when the nodes move without a relayout -- which today means a user drag.  A route touching
 * one of the moved nodes is thrown away rather than translated: it was computed for the old
 * positions, and a route that no longer joins the boxes it was drawn for is worse than one
 * routed afresh.  The rest of the chart keeps the routes the engine gave it, so dragging one
 * node no longer costs every other link its geometry -- see `attachEdgeGeometry`, which decides
 * this per edge.  Everything else is shared with `getLayoutedElements`, so a dragged chart and a
 * laid-out one are routed by exactly the same code.
 *
 * Node boxes come from what react-flow is actually rendering -- `style.width`/`style.height`, as
 * written by the last layout, falling back to the live measurement.  Notably a group's box is NOT
 * re-derived from its children the way the `manual` branch of `getLayoutedElements` does it: after
 * an automatic layout that box came from ELK, and recomputing it here would shift the band
 * obstacles out from under the bands the user can see.
 */
const rerouteEdges = (
    nodes: Node<any, string>[],
    edges: Edge[],
    options: ResolvedLayoutOptions,
    /** The nodes the user moved. Everything else keeps the route it already has. */
    movedNodes: Set<string>,
    headings?: HeadingSizes
): Edge[] => {
    const boxes = new Map<string, LayoutRect>(nodes.map(node => [node.id, {
        x: node.position?.x ?? 0,
        y: node.position?.y ?? 0,
        width: renderedWidth(node),
        height: renderedHeight(node),
    }]));

    // Dragging a GROUP moves its children too, and the caller cannot know that. A child's
    // `position` is parent-relative, so moving the band leaves every child's position untouched
    // and react-flow reports a change for the band alone -- while every child's ABSOLUTE box, the
    // only one routing works in, has moved by the whole drag. The links to and from those children
    // then matched neither test: not moved, so they took the preserved-route path and were handed
    // back pointing at where their endpoints used to be.
    //
    // Answered by walking UP rather than enumerating descendants, so nesting costs nothing extra:
    // a node has moved if it or anything it sits inside has.
    const parents = parentOf(nodes);
    const carried = new Set(nodes
        .map(node => node.id)
        .filter(id => [...selfAndAncestors(id, parents)].some(ancestor => movedNodes.has(ancestor))));

    const manual = options.layout ? options.layout === "manual" : nodes.some(n => n.data?.hasAuthoredPosition);
    return attachEdgeGeometry(nodes, edges, boxes, new Map(), options, carried, headings, manual);
};

/**
 * Annotate each edge with the crossings it is responsible for indicating.
 *
 * Runs once over every edge's final geometry, because a crossing is a property of a pair of
 * links rather than of either one -- which is why this cannot live in the edge component.
 */
const withCrossings = (edges: Edge[], nodeRects: LayoutRect[], hiddenNodes: Set<string>): Edge[] => {
    const inputs: RouteInput[] = edges.map((edge) => {
        const route = (edge.data?.route as Point[] | undefined) ?? [];
        const crossing = (edge.data?.crossing as string | undefined) ?? "none";
        const direct = edge.data?.routing === "direct";

        return {
            id: edge.id,
            points: route,
            width: Number((edge.style as any)?.strokeWidth) || defaultEdgeOptions.width,
            // A `straight` link is a deliberate chord rather than an orthogonal route, so it is
            // neither crossed nor crossing; likewise anything with no geometry.  `routing:
            // "direct"` is the same case: the renderer discards the route (edgeTypes/factory.tsx),
            // so the geometry here is never drawn -- letting it take part would put indicators
            // where nothing is visible, and letting it OWN a crossing loses the indicator
            // entirely, since a direct link has no route to draw one on.  An edge hanging off a
            // hidden node is the same case at one remove: react-flow renders nothing for it, so
            // its geometry must not earn anyone a bridge over a line that is not there.
            eligible: route.length >= 2
                && edge.type !== "straight"
                && !edge.hidden
                && !direct
                && !hiddenNodes.has(edge.source)
                && !hiddenNodes.has(edge.target),
            wantsHops: (crossing === "casing" || crossing === "hop") && !direct,
            source: edge.source,
            target: edge.target,
        };
    });

    const hops = computeHops(inputs, nodeRects);

    // Authoritative rather than additive: an edge whose crossings have GONE -- because the links
    // were pulled apart, or the one it crossed was deleted or hidden -- has to lose the indicators
    // it was carrying, or it goes on drawing a bridge over thin air.  Casings are the worse half
    // of that: they paint in the background colour at absolute coordinates the route no longer
    // passes through, so a stale one erases part of whatever link now runs underneath it.  Nothing
    // upstream clears the key -- `attachEdgeGeometry` hands a preserved edge straight back, and
    // rebuilds `data` by spread otherwise -- so this pass owns it outright, and that means writing
    // the empty case too.
    return edges.map((edge) => {
        const next = hops.get(edge.id);
        const current = edge.data?.hops;

        // Identity matters: react-flow memoises an edge on its `data`, so handing back the very
        // same object is what keeps an untouched link from re-rendering on every reroute.
        if (!next && !current) {
            return edge;
        }

        if (!next) {
            const { hops: _cleared, ...data } = edge.data ?? {};
            return { ...edge, data };
        }

        return { ...edge, data: { ...edge.data, hops: next } };
    });
};

/** A node and every group it sits inside. */
const selfAndAncestors = (id: string, parents: Map<string, string | undefined>) => {
    const chain = new Set([id]);
    for (let parent = parents.get(id); parent != null; parent = parents.get(parent)) {
        chain.add(parent);
    }
    return chain;
};

/**
 * Arrange the unconnected children of one level: same start, same length, declaration order.
 *
 * Where nothing links a level's children -- every band of a plant diagram, every level of an
 * organisational tree -- ELK sees unconnected components and packs them centred, ragged and in an
 * order of its own.  Groups are stretched to the longest; a bare node is stacked with them but keeps
 * its width.  Children of a child and the routes inside it are positioned relative to it, so they
 * move with it; there are no routes AT this level to move.  Returns the level's new size, or
 * nothing when ELK's arrangement stands.
 */
const stackSiblings = (
    children: Node<any, string>[],
    boxes: Map<string, LayoutRect>,
    options: ResolvedLayoutOptions,
    /** The level's padding: `top` above the first child, `side` everywhere else. */
    padding: { top: number; side: number }
): { width: number; height: number } | undefined => {
    const stacked = children.filter(child => boxes.has(child.id));
    const bands = new Set(stacked.filter(child => child.type === "group").map(child => child.id));
    if (stacked.length < 2 || !bands.size) {
        return undefined;
    }

    const flow: "x" | "y" = options.rankdir === "TB" || options.rankdir === "BT" ? "y" : "x";
    const cross: "x" | "y" = flow === "x" ? "y" : "x";
    const flowExtent = flow === "x" ? "width" : "height";
    const crossExtent = cross === "x" ? "width" : "height";

    // The heading sits above the children whichever way the flow runs, so the padding on the
    // y axis is `top` and on the x axis `side`.
    const start = flow === "y" ? padding.top : padding.side;
    let offset = cross === "y" ? padding.top : padding.side;
    // Stretch every band to the longest, so they read as swimlanes rather than a ragged stack.
    const longest = Math.max(...[...bands].map(id => boxes.get(id)![flowExtent]));
    let reach = 0;

    for (const child of stacked) {
        const box = boxes.get(child.id)!;
        const next = { ...box, [flow]: start, [cross]: offset };
        if (bands.has(child.id)) {
            next[flowExtent] = longest;
        }
        boxes.set(child.id, next);

        reach = Math.max(reach, next[flowExtent]);
        offset += next[crossExtent] + options.nodesep;
    }

    const size = { width: 0, height: 0 };
    size[flowExtent] = start + reach + padding.side;
    size[crossExtent] = offset - options.nodesep + padding.side;
    return size;
};

/** Accept either a marker name or a full marker object, and always emit an object. */
const normaliseMarker = (
    marker: MarkerKind | EdgeMarker | null | undefined,
    color: string
): EdgeMarker | undefined => {
    if (marker == null) {
        return undefined;
    }

    // An object marker already names its own type; `color` is only a default it may override.
    return typeof marker === "object" ? { color, ...marker } : { type: marker, color };
};

/** id -> parentId, for resolving parent-relative coordinates. */
const parentOf = (nodes: Node<any, string>[]) =>
    new Map(nodes.map(n => [n.id, n.parentId as string | undefined]));

/**
 * Resolve a node's rect into absolute coordinates by walking up its ancestors.
 *
 * Both layout branches produce parent-relative positions, but handle selection compares nodes
 * that may sit in different groups, so the comparison has to happen in one shared space.
 */
const absoluteRect = (
    id: string,
    rects: Map<string, LayoutRect>,
    parents: Map<string, string | undefined>
): LayoutRect | undefined => {
    const rect = rects.get(id);
    if (!rect) {
        return undefined;
    }

    let { x, y } = rect;
    for (let parentId = parents.get(id); parentId != null; parentId = parents.get(parentId)) {
        const parent = rects.get(parentId);
        if (!parent) break;
        x += parent.x;
        y += parent.y;
    }

    return { x, y, width: rect.width, height: rect.height };
};

const mapInputData = (input: FlowChartNode[], getNodeContent: (node) => any, getEdgeContent: (edge) => any) => {
    const nodes: Node<any, string>[] = [];
    const edges: Edge<any>[] = [];
    const edgeIds = new Set<string>();

    function processNode(node: FlowChartNode, parent?: FlowChartNode) {
        // Add Node...
        const { id, type, hidden, links, children, className, style, ...data } = node;
        nodes.push({
            ...defaultNodeOptions,
            id,
            parentId: parent?.id,
            type: type ?? defaultNodeOptions.type,
            hidden,
            className,
            style,
            // `hidden` and `links` are documented as reaching `getNodeContent` but were being
            // destructured away above, so a callback that branched on either saw `undefined`.
            // `children` stays out on purpose -- it would duplicate the whole subtree onto every
            // node, and the render-state key in index.tsx walks this object.
            data: {
                ...data,
                id,
                parentId: parent?.id,
                type: type ?? defaultNodeOptions.type,
                hidden: hidden ?? false,
                links: links ?? [],
                getContent: getNodeContent,
                // Whether the author supplied coordinates at all.  `position` below coerces a
                // missing one to 0, which makes an explicit `x: 0` indistinguishable from
                // "unset" -- so "manual, at the origin" was unexpressible.  Captured here while
                // the input is still intact.
                hasAuthoredPosition: node.x != null || node.y != null
            },
            position: {
                x: node.x ?? 0,
                y: node.y ?? 0
            }
        });

        // Add Edges...
        (links ?? []).forEach(link => {
            const { id: _linkId, from, to, routing, crossing, markerStart: _markerStart, markerEnd: _markerEnd, color: _color, width: _width, label, template, content, style, positionStart, positionEnd, excludeFromLayout: _excludeFromLayout, ...rest } = link;
            const source = from ?? id;
            const target = to ?? id;
            const color = _color ?? defaultEdgeOptions.color;
            const width = _width ?? defaultEdgeOptions.width;
            const excludeFromLayout = _excludeFromLayout ?? defaultEdgeOptions.excludeFromLayout;

            // react-flow accepts either a marker name or a full `{ type, width, height, color }`
            // object.  Wrapping the latter in `{ type: ... }` nests an object where a name is
            // expected, and the arrowhead then silently fails to render -- valid input turned
            // invalid by us, so this is a fix rather than a documented default.
            const markerStart = normaliseMarker(_markerStart, color);
            // `??` rather than a default here would collapse `null` into `undefined` and hand back
            // the `arrowclosed` the author explicitly asked to remove -- and with no other way to
            // suppress it, a link documented as arrowless kept its arrowhead.  Only an ABSENT
            // `markerEnd` takes the default.
            const markerEnd = normaliseMarker(
                _markerEnd === undefined ? defaultEdgeOptions.markerEnd : _markerEnd, color);

            if (source === target) {
                return;
            }

            // An author-supplied id lets two links share a source and target -- with different
            // routing, say.  Without one the pair is the key, which is why a second link between
            // the same two nodes used to disappear.
            const edgeId = link.id ?? `${source}-${target}`;

            // Don't add duplicate edges...
            if (edgeIds.has(edgeId)) {
                return;
            }
            edgeIds.add(edgeId);

            const resolvedStyle = { stroke: color, strokeWidth: width, ...style };

            edges.push({
                ...reactFlowEdgeDefaults,
                ...rest,
                id: edgeId,
                source,
                target,
                markerStart,
                markerEnd,
                excludeFromLayout,
                style: resolvedStyle,
                // What `getEdgeContent` is handed.  The documented shape is `{ from, to, type,
                // color, width, style, animated, markerStart, markerEnd, excludeFromLayout, ... }`
                // and for a long time only about half of that was actually here -- `source`/
                // `target` in place of `from`/`to`, and no paint or marker fields at all, so a
                // callback written against the documentation read `undefined`.  Both spellings
                // are present now; nothing that was here has been taken away.
                //
                // `children` is deliberately absent: it would copy the whole subtree onto every
                // node, and the render-state key in index.tsx walks this object.
                data: {
                    id: edgeId,
                    source,
                    target,
                    from: source,
                    to: target,
                    type: rest.type ?? defaultEdgeOptions.type,
                    color,
                    width,
                    style: resolvedStyle,
                    animated: rest.animated ?? false,
                    markerStart,
                    markerEnd,
                    excludeFromLayout,
                    label,
                    template,
                    content,
                    getContent: getEdgeContent,
                    className: rest.className,
                    positionStart,
                    positionEnd,
                    routing,
                    crossing
                }
            });
        });

        // Add Children...
        (children ?? []).forEach(child => {
            processNode(child, node);
        });
    }

    input.forEach(node => processNode(node));

    return {
        nodes,
        edges
    };
};


/*
 * Nothing here is deleted lightly.  `library_build` runs vite-plugin-dts WITHOUT `rollupTypes`, so
 * this module is emitted as `dist/src/viewer/components/FlowChart/layout.d.ts` and every name below
 * is reachable by tenant plugins through @qerent/lib's `"./*"` subpath.  Adding is free; removing or
 * renaming is a breaking change.  See the note at the top of `src/library.ts`.
 */

export type {
    FlowChartLink, FlowChartNode, FlowNode, LayoutRect, LayoutEngine, Point, LayoutResult, HeadingSize, HeadingSizes,
    HandlePosition, MarkerKind, EdgeMarker, HandleMapping, LayoutOptions, ResolvedLayoutOptions, Edge,
    /** @deprecated alias of `HandlePosition`. */
    HandleLocation,
};

export { defaultLayoutOptions, defaultNodeOptions, defaultEdgeOptions, reactFlowEdgeDefaults };

/** Used by the component itself (`index.tsx`). */
export { getLayoutedElements, rerouteEdges, mapInputData, mergeLayoutOptions };

/**
 * Exported so `layout.test.ts` can exercise them directly -- the routing helpers are pure geometry
 * and are far cheaper to test on their own than through a rendered chart.  Not part of the
 * component's own interface, and NOT published: `library_build` sets `stripInternal`, so the
 * `@internal` tag below deletes this whole statement from the emitted `layout.d.ts`.  Without it
 * each name here resolved as `@qerent/lib/src/viewer/components/FlowChart/layout`, and by the rule
 * above renaming any one of them would then have been a breaking change.
 *
 * @internal
 */
export { includeEdgeInLayout, DEPRECATED_RANKERS, toElkOptions, layoutByLevel, requiredPorts, routeOf, sideOfPoint, portId, stackSiblings, selfAndAncestors, seatEndpoints, pinArrival, normaliseMarker, attachEdgeGeometry, getEdgeHandles, nodeWidth, nodeHeight, absoluteRect, parentOf };
